import { useEffect, useRef, useState, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import IntroCinematic from "@/components/IntroCinematic";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Team from "@/pages/Team";
import Legacy from "@/pages/Legacy";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Happening from "@/pages/Happening";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Publications from "@/pages/Publications";
import CalendarPage from "@/pages/CalendarPage";
import Gallery from "@/pages/Gallery";
import Achievements from "@/pages/Achievements";
import Contact from "@/pages/Contact";
import NorthStar from "@/pages/NorthStar";
import RotaryClub from "@/pages/RotaryClub";
import Collaborators from "@/pages/Collaborators";
import Login from "@/pages/Login";
import Members from "@/pages/Members";
import Admin from "@/pages/Admin";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink px-6 text-center">
          <div>
            <p className="eyebrow">Something went wrong</p>
            <p className="mt-4 font-display text-3xl italic text-silver">This section could not be displayed.</p>
            <button onClick={() => window.location.reload()} className="btn-silver mt-8" data-testid="reload-btn">
              <span>Reload</span>
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager({ lenisRef }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter({ introDone }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home introDone={introDone} />} />
      <Route path="/about" element={<About />} />
      <Route path="/team" element={<Team />} />
      <Route path="/legacy" element={<Legacy />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/projects/:id" element={<ProjectDetail />} />
      <Route path="/what-are-we-up-to" element={<Happening />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:id" element={<BlogDetail />} />
      <Route path="/publications" element={<Publications />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/achievements" element={<Achievements />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/our-north-star" element={<NorthStar />} />
      <Route path="/rotary-club-of-bombay-central" element={<RotaryClub />} />
      <Route path="/collaborators" element={<Collaborators />} />
      <Route path="/login" element={<Login />} />
      <Route path="/members" element={<Members />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

export default function App() {
  const [introDone, setIntroDone] = useState(() => {
    try {
      return sessionStorage.getItem("rcscw-intro") === "1";
    } catch (e) {
      return false;
    }
  });
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const l = lenisRef.current;
    if (!l) return;
    if (introDone) l.start();
    else l.stop();
  }, [introDone]);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollManager lenisRef={lenisRef} />
        {!introDone && <IntroCinematic onDone={() => setIntroDone(true)} />}
        <Navbar introDone={introDone} />
        <AppRouter introDone={introDone} />
        <Footer />
        <ChatWidget />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
